import { DuplicateIndicator, IssueIndicator } from "@/components/CaseListing/IssueIndicator";
import { ListingPatientScanInfo } from "@/components/CaseListing/ListingPatientScanInfo";
import { canDrop, ROW_HEIGHT } from "@/components/CaseListing/utils";
import { CloseSvg, MoreMenuSvg, ViewSvg } from "@/components/Icon/Icon";
import { Menu } from "@/components/Menu/Menu";
import {
  addSelectedScan,
  deleteCaseUpload,
  deleteScanUpload,
  moveSelectedScans,
  rangeSelectScans,
  selecteNumScans,
  selectIsScanSelected,
  selectScan,
  setDraggingOver,
  setInspecting,
  setIsInspecting,
  setScanLabel,
  setSelectedScan,
  toggleSelectedScan
} from "@/store/slices/casesSlice";
import { RootState, store } from "@/store/store";
import { Box, IconButton, MenuItem, Select, TableCell, TableRow, Theme, Typography, useTheme } from "@mui/material";
import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const ScanTypeSelector = ({ uploadId, scanId, onClose }: { uploadId: number; scanId: string; onClose?: () => void }) => {
  const { t } = useTranslation("cases");
  const scan = useSelector((state: RootState) => selectScan(state, scanId));
  const label = (scan?.label === "combined" ? undefined : scan?.label) ?? "";
  const [defaultLabel] = useState<"upper" | "lower" | "">(label);

  return (
    <>
      <Select<"lower" | "upper" | "">
        displayEmpty
        onClick={(e) => e.stopPropagation()}
        onChange={async (e) => {
          await store.dispatch(setScanLabel({ uploadId, scanId: scanId, label: e.target.value as any }));
        }}
        onClose={onClose}
        defaultValue={defaultLabel}
        renderValue={(value) => {
          return t(`uploadScans.${value}`, { defaultValue: t("uploadScans.unknown") });
        }}
        sx={{
          fontSize: "inherit",
          ".MuiSelect-select": {
            paddingLeft: "6px"
          },
          color: (theme) => theme.palette.text.secondary,
          bgcolor: (theme) => theme.surface.lowest
        }}
        size="small"
        fullWidth
      >
        <MenuItem sx={{ display: "none" }} value=""></MenuItem>
        <MenuItem value="lower">{t("uploadScans.lower")}</MenuItem>
        <MenuItem value="upper">{t("uploadScans.upper")}</MenuItem>
      </Select>
    </>
  );
};

const ScanType = ({ scanId }: { scanId: string }) => {
  const { t } = useTranslation("cases");
  const scan = useSelector((state: RootState) => selectScan(state, scanId));

  if (scan === undefined) {
    return <></>;
  }

  return (
    <Typography
      sx={{
        flex: 1,
        paddingY: "2px",
        userSelect: "none",
        cursor: "text",
        borderRadius: "4px",
        paddingLeft: "6px",
        lineHeight: "39px",
        color: (theme) => theme.palette.text.secondary
      }}
      noWrap
    >
      {t(`uploadScans.${scan.label}`, { defaultValue: t("uploadScans.unknown") })}
    </Typography>
  );
};

interface ScanListingProps {
  uploadId: number;
  scanId: string;
  status?: string;
  setStatus?: Dispatch<SetStateAction<string>>;
  disabled?: boolean; // disabled scans are not based, displayed with reduced opacity
}

interface ScanInspectionButtonProps {
  caseId: number;
  scanId: string;
  visible?: boolean;
}

const ScanInspectionButton: React.FC<ScanInspectionButtonProps> = ({ caseId, scanId, visible }) => {
  const theme = useTheme();
  return (
    <Box>
      <IconButton
        sx={{
          visibility: visible ? "visible" : "hidden"
        }}
        onClick={(e) => {
          e.stopPropagation();
          store.dispatch(setInspecting({ caseIndex: caseId, scanId: scanId ?? null }));
          store.dispatch(setIsInspecting({ isInspecting: true }));
        }}
      >
        <ViewSvg color={theme.surface.onVariant} />
      </IconButton>
    </Box>
  );
};

const ScanMenu = ({ visible, uploadId, scanId }: { visible?: boolean; uploadId: number; scanId: string }) => {
  const theme = useTheme();
  const { t } = useTranslation("cases");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
  };
  const handleMoveToNewCase = () => {
    store.dispatch(addSelectedScan({ uploadId, scanId }));
    store.dispatch(moveSelectedScans({ newCaseName: t("title.newCase") }));
  };
  return (
    <>
      <IconButton onClick={handleMenuClick}>
        <MoreMenuSvg style={{ visibility: visible ? "visible" : "hidden" }} color={theme.surface.onVariant} />
      </IconButton>
      <Menu
        innerProps={{
          anchorEl: anchorEl,
          open: openMenu,
          onClose: handleMenuClose,
          onClick: handleMenuClose
        }}
        hideArrow
        sx={{
          mt: undefined,
          minWidth: undefined,
          borderRadius: "4px",
          paddingY: "8px",
          padding: "0",
          "& > ul": {
            padding: "0px"
          }
        }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleMoveToNewCase();
          }}
          sx={{ paddingX: "16px" }}
        >
          {t("button.moveToNewCase")}
        </MenuItem>
      </Menu>
    </>
  );
};

export const ScanListing: FC<ScanListingProps> = ({ status, uploadId, scanId, setStatus, disabled = false }) => {
  const theme = useTheme();
  const [hover, setHover] = useState<boolean>(false);
  const numScans = useSelector((state: RootState) => selecteNumScans(state, uploadId));
  const handleDeleteScan = async () => {
    if (numScans === 1) {
      // Deleting the last scan of the case -> delete the case
      await store.dispatch(deleteCaseUpload(uploadId));
    } else {
      await store.dispatch(deleteScanUpload([uploadId, scanId]));
    }
  };
  const isSelected = useSelector((state: RootState) => selectIsScanSelected(state, uploadId, scanId));
  const [_, drag, preview] = useDrag(() => ({
    type: "scan",
    item: { uploadId, scanId }
  }));
  const [{ isOver, isDragging }, drop] = useDrop(() => ({
    accept: "scan",
    canDrop: canDrop(uploadId),
    collect: (monitor) => ({
      isOver: monitor.isOver() && monitor.canDrop(),
      isDragging: monitor.getItem() != null && monitor.getItemType() === "scan"
    })
  }));

  useEffect(() => {
    store.dispatch(setDraggingOver({ uploadId, isOver: isOver, overScan: true }));
  }, [isOver, uploadId]);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true }); // disable drag image (we will show our own overlay)
  }, [preview]);

  const handleSelecting = (e: { ctrlKey: boolean; shiftKey: boolean }) => {
    if (e.ctrlKey) {
      store.dispatch(toggleSelectedScan({ uploadId, scanId }));
    } else if (e.shiftKey) {
      store.dispatch(rangeSelectScans({ uploadId, scanId }));
    } else {
      store.dispatch(setSelectedScan({ uploadId, scanId }));
    }
  };

  return (
    <TableRow
      ref={(node) => drag(drop(node))}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onDrop={() => store.dispatch(moveSelectedScans({ uploadId }))}
      onDragStart={(e) => {
        if (isSelected) {
          return;
        }
        handleSelecting(e);
      }}
      sx={{
        height: `${ROW_HEIGHT}px`,
        borderBottom: (theme: Theme) => `2px solid ${theme.outline.main}`,
        backgroundColor: (theme: Theme) => (hover || isSelected ? theme.surface.normal : theme.surface.lowest)
      }}
      onClick={(e) => {
        e.stopPropagation();
        handleSelecting(e);
      }}
    >
      <TableCell padding="checkbox"></TableCell>
      <TableCell padding="checkbox"></TableCell>
      <TableCell sx={{ maxWidth: "250px", minWidth: "250px" }}>
        <ListingPatientScanInfo
          uploadId={uploadId}
          scanId={scanId}
          status={status}
          setStatus={setStatus}
          disabled={disabled}
          isDragging={isSelected && isDragging}
        />
      </TableCell>
      <TableCell sx={{ minWidth: "115px" }}>
        {hover ? <ScanTypeSelector uploadId={uploadId} scanId={scanId} onClose={() => setHover(false)} /> : <ScanType scanId={scanId} />}
      </TableCell>
      <TableCell>
        <DuplicateIndicator uploadId={uploadId} scanId={scanId} />
      </TableCell>
      <TableCell>
        <IssueIndicator uploadId={uploadId} scanId={scanId} />
      </TableCell>
      <TableCell>
        <ScanInspectionButton visible={hover} caseId={uploadId} scanId={scanId} />
      </TableCell>
      <TableCell>
        <IconButton
          sx={{ visibility: hover ? "visible" : "hidden" }}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteScan();
          }}
          size="small"
          color="inherit"
        >
          <CloseSvg color={theme.surface.onVariant} />
        </IconButton>
      </TableCell>
      <TableCell sx={{ paddingRight: "8px !important" }}>
        <ScanMenu visible={hover} scanId={scanId} uploadId={uploadId} />
      </TableCell>
    </TableRow>
  );
};
